import React, { useCallback, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import clsx from 'clsx';
import Select from 'react-select';

import FormError from '../../../../shared/components/errors/FormError';
import FormSpinner from '../../../../shared/components/spinner/FormSpinner';
import UpdateRestrictionSchema, { AllowedTypeValues, UpdateRestrictionSchemaPayload } from '../../../../services/availability-and-rates/schema/UpdateRestrictionSchema';
import { useUpdateRestriction } from '../../../../services/availability-and-rates/hooks/useUpdateRestriction';
import { SelectedCell } from '../MonthlySetupTable'; // Pastikan path import benar

// Tipe untuk opsi di React-Select
interface OptionType {
    value: AllowedTypeValues;
    label: string;
}

// Props yang diterima oleh komponen
interface FormRestrictionProps {
    isError: boolean;
    isFetching: boolean;
    isSuccess: boolean;
    rateplan: any; // Tipe dari useShowRatePlan
    selection: SelectedCell[];
    onClose: () => void;
    onSuccess: () => void;
    onTypeChange: (type: string) => void;
}

const FormRestriction: React.FC<FormRestrictionProps> = ({ isError, isFetching, isSuccess, rateplan, selection, onClose, onSuccess, onTypeChange }) => {
    const options: OptionType[] = [
        { value: 'rate', label: 'Rate' },
        { value: 'closed_to_arrival', label: 'Closed to Arrival' },
        { value: 'closed_to_departure', label: 'Closed to Departure' },
        { value: 'stop_sell', label: 'Stop Sell' },
        { value: 'max_stay', label: 'Max Stay' },
        { value: 'min_stay_arrival', label: 'Min Stay Arrival' },
        { value: 'min_stay_through', label: 'Min Stay Through' },
    ];

    // Tentukan mode input berdasarkan tipe restriksi
    const switchMode = ['stop_sell', 'closed_to_arrival', 'closed_to_departure'];
    const inputMode = ['max_stay', 'min_stay_arrival', 'min_stay_through'];

    // Ambil info dari sel pertama (karena sudah divalidasi bahwa semua sama)
    const firstCell = selection[0];
    const { rateId, restrictionType } = firstCell;

    const { register, handleSubmit, formState: { errors }, control, setError, reset, watch } = useForm<UpdateRestrictionSchemaPayload>({
        resolver: zodResolver(UpdateRestrictionSchema),
    });

    // Mengatur nilai awal form ketika 'selection' berubah
    useEffect(() => {
        if (selection.length > 0) {
            // Buat array dari rentang tanggal. Setiap sel adalah rentang 1 hari.
            const dateRanges = selection.map(cell => ({
                date_from: cell.date,
                date_to: cell.date
            }));

            reset({
                rate_plan_id: rateId,
                type: restrictionType as AllowedTypeValues,
                date_ranges: dateRanges,
                value: "0" // Biarkan kosong agar diisi oleh pengguna
            });
        }
    }, [selection, rateId, restrictionType, reset]);

    const { mutate, isPending } = useUpdateRestriction();
    const queryClient = useQueryClient();

    const onSubmit = useCallback((data: UpdateRestrictionSchemaPayload) => {
        toast.loading('Saving Restrictions...', { id: 'save-restrictions' });

        mutate({ values: data }, {
            onSuccess: async () => {
                // Refetch query yang relevan untuk memperbarui UI.
                // 'price-adjustment' WAJIB ikut supaya baris DPM ikut ter-update —
                // queryKey-nya tidak mengandung 'monthly'/'occupancy'.
                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['monthly', 'occupancy', 'price-adjustment'].includes(key as string))
                });
                toast.success('Restrictions saved successfully', { id: 'save-restrictions' });
                onSuccess(); // Panggil onSuccess untuk menutup modal dan membersihkan state di parent
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof UpdateRestrictionSchemaPayload, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                    }
                    toast.error('Failed to save restrictions', { id: 'save-restrictions' });
                } else {
                    toast.error(mutationError?.message || 'Failed to save restrictions', { id: 'save-restrictions' });
                }
            }
        });
    }, [mutate, queryClient, onSuccess, setError]);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Modal.Body>
                {isError && <FormError />}
                {isFetching && <FormSpinner />}
                {isSuccess && !isFetching && rateplan && (
                    <>
                        <div className="row mb-1 align-items-center">
                            <span className="col-sm-4 col-form-label d-flex justify-content-end">Room Type: </span>
                            <span className="col-sm-8 col-form-label">{rateplan.data.room_type?.name}</span>
                        </div>
                        <div className="row mb-1 align-items-center">
                            <span className="col-sm-4 col-form-label d-flex justify-content-end">Rate Plan: </span>
                            <span className="col-sm-8 col-form-label">{rateplan.data.name}</span>
                        </div>
                        <div className="row mb-3 align-items-center">
                            <span className="col-sm-4 col-form-label d-flex justify-content-end">Selected Dates: </span>
                            <span className="col-sm-8 col-form-label fw-bold">{selection.length} date(s) selected</span>
                        </div>

                        <div className='row mb-1 align-items-center'>
                            <label htmlFor='type' className='col-sm-4 col-form-label d-flex justify-content-end' style={{ fontSize: '14px' }}>Setup: </label>
                            <div className='col-sm-8'>
                                <Controller
                                    name='type'
                                    control={control}
                                    render={({ field }) => (
                                        <Select<OptionType>
                                            {...field}
                                            options={options}
                                            value={field.value ? options.find(option => option.value === field.value) : null}
                                            onChange={(selected) => {
                                                field.onChange(selected?.value)
                                                onTypeChange(selected?.value ?? '')
                                            }}
                                            isDisabled={true} // Tipe tidak bisa diubah dalam mode bulk edit
                                        />
                                    )}
                                />
                                {errors.type && <div className='invalid-feedback'>{(errors.type as FieldError).message}</div>}
                            </div>
                        </div>

                        {/* Input untuk 'value' yang akan diterapkan ke semua tanggal yang dipilih */}
                        {switchMode.includes(watch('type') as string) && (
                            <div className='row mb-1 align-items-center'>
                                <label className='col-sm-4 col-form-label d-flex justify-content-end' style={{ fontSize: '14px' }}>Set for All: </label>
                                <div className='col-sm-8 d-flex align-items-center justify-content-start'>
                                    <Controller
                                        name='value'
                                        control={control}
                                        defaultValue="0" // Default ke 'Inactive'
                                        render={({ field }) => (
                                            <label className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={field.value === "1"}
                                                    onChange={(e) => field.onChange(e.target.checked ? "1" : "0")}
                                                />
                                                <span className='form-check-label'>{field.value === "1" ? "Active" : "Inactive"}</span>
                                            </label>
                                        )}
                                    />
                                    {errors.value && <div className='invalid-feedback d-block'>{(errors.value as FieldError).message}</div>}
                                </div>
                            </div>
                        )}
                        {inputMode.includes(watch('type') as string) && (
                            <div className='row mb-1 align-items-center'>
                                <label className='col-sm-4 col-form-label d-flex justify-content-end' style={{ fontSize: '14px' }}>Set Value for All: </label>
                                <div className='col-sm-8'>
                                    <input
                                        type='number'
                                        min="0"
                                        {...register('value')}
                                        className={clsx("form-control app-form-control", errors.value && 'is-invalid')}
                                        placeholder="Enter value"
                                    />
                                    {errors.value && <div className='invalid-feedback'>{(errors.value as FieldError).message}</div>}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                    Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isFetching || isPending}>
                    Apply Update
                </button>
            </Modal.Footer>
        </form>
    )
}

export default FormRestriction;